import { useState, useMemo } from 'react';
import { TableProps } from 'rsuite';

interface SortFn {
  <T extends Record<string, any>>(
    itemA: T[keyof T],
    itemB: T[keyof T],
    sortType: TableProps['sortType'],
  ): number;
}

const defaultSortFn: SortFn = (valueA, valueB, sortType = 'asc') => {
  switch (sortType) {
    case 'desc': {
      return valueA < valueB ? 1 : -1;
    }
    case 'asc':
    default: {
      return valueA > valueB ? 1 : -1;
    }
  }
};

export const useTableSort = <T extends Record<string, any>>(
  data: T[],
  sortFns?: Partial<Record<keyof T, SortFn>>,
) => {
  const [sortColumn, setSortColumn] = useState<keyof T>();
  const [sortType, setSortType] = useState<TableProps['sortType']>();

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortType) return data;

    const sortFn = sortFns?.[sortColumn] || defaultSortFn;

    return [...data].sort((itemA, itemB) =>
      sortFn(itemA[sortColumn], itemB[sortColumn], sortType),
    );
  }, [data, sortColumn, sortType, sortFns]);

  const handleSortColumn: TableProps['onSortColumn'] = (
    sortColumn,
    sortType,
  ) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  return {
    sortedData,
    sortColumn,
    sortType,
    handleSortColumn,
  };
};
